import { APIService } from "../../shared/shared-with-mobile/api-client/api.service";
import {
  AnalyticsResponse,
  CustomerDetailResponse,
  EngagementRow,
} from "../../shared/shared-with-mobile/api-client/services/admin-analytics.service";
import { LicenseHolderModel } from "../../generated/from-api/models/license.model";
import { LicenseUsageModel } from "../../generated/from-api/models/license-usage.model";
import { LICENSE_TIERS } from "../../generated/from-api/api-constants/license-tier-constants";

// Build a lookup table from tier UUID -> friendly user-facing name.
// LICENSE_TIERS is keyed by short codes like "GOLD" / "GOLD2023" with each
// value carrying { id, name, userFacingSubscriptionName }. Charts that
// receive a raw tier id should display the friendly name instead.
const TIER_ID_TO_FRIENDLY: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const tier of Object.values(LICENSE_TIERS)) {
    if (tier.id) map[tier.id] = tier.userFacingSubscriptionName || tier.name;
    if (tier.name)
      map[tier.name] = tier.userFacingSubscriptionName || tier.name;
  }
  return map;
})();

export function friendlyTierName(idOrName: string | null | undefined): string {
  if (!idOrName) return "Unknown";
  return TIER_ID_TO_FRIENDLY[idOrName] ?? idOrName;
}

// Shape the Admin UI expects. Intentionally matches the old mock
// AdminDataService shape so downstream components render unchanged.
export interface AdminLicenseHolder {
  owner: string;
  email: string;
  tier: string;
  payment: string;
  hubspotPayment: string;
  tnRep: string;
  dateCreated: string;
  users: number;
  questions: number;
  teamName: string;
  teamLevel: string;
  teamSetup: string;
  setupCount: number;
  healthScore: number;
  healthLabel: "Healthy" | "At Risk" | "Critical";
  // Optional engagement metrics — populated when the engagement endpoint
  // resolves alongside the base load. Undefined while loading or on
  // backend failure (UI should render "—" in that case).
  questions7d?: number;
  questions60d?: number;
  questions90d?: number;
  questionsPrev90d?: number;
  questions1y?: number;
  lastCoachActivityAt?: string | null;
  lastPlayerActivityAt?: string | null;
  // Lifetime $ paid via Shopify, matched by email. Populated by the
  // engagement endpoint when Shopify data is imported. 0 (not undefined)
  // when the customer has no paid Shopify orders.
  shopifyPaidTotal?: number;
  // Link status — "yes" if at least one external_link with status=
  // confirmed exists for this license-holder + platform; "suggested"
  // if a suggested-only link exists; "no" if neither.
  shopifyLink?: "yes" | "suggested" | "no";
  hubspotLink?: "yes" | "suggested" | "no";
  // Team city + state from the team table (joined via license_usage).
  // Combined into a Location column on the Customer Engagement table.
  teamCity?: string | null;
  teamState?: string | null;
  // Coach + player counts split out from the combined `users` total, so
  // the engagement table can show them as separate sub-columns. Available
  // both from license_usage (for the base build) and the engagement
  // endpoint (overrides if present).
  totalCoaches?: number;
  totalPlayers?: number;
  // Communication counts: HubSpot historical engagements + this
  // platform's SendGrid/Twilio send log. See engagement.service.ts for
  // exact attribution (outbound = SendGrid sends + HubSpot OUTBOUND
  // emails/calls; inbound = HubSpot INBOUND only until we wire SendGrid
  // Inbound Parse + Twilio inbound; logged = HubSpot notes + meetings).
  commsOutbound?: number;
  commsInbound?: number;
  commsLogged?: number;
  commsTotal?: number;
}

export interface AdminPlatformMetrics {
  totalAccounts: number;
  paidAccounts: number;
  totalQuestions: number;
  totalUsers: number;
  avgHealthScore: number;
  healthyCount: number;
  atRiskCount: number;
  criticalCount: number;
  newThisMonth: number;
}

export interface AdminSnapshotSummary {
  date: string;
  totalAccounts: number;
  paidAccounts: number;
  totalQuestions: number;
  totalUsers: number;
  topTiers: { tier: string; count: number }[];
}

function parseSetupPercent(setup: string): number {
  if (!setup) return 0;
  const m = String(setup).match(/(\d+)/);
  return m ? Math.min(100, Math.max(0, Number(m[1]))) / 100 : 0;
}

// Mirror of the mock-era scoring rubric so dev/prod feel visually identical.
function calculateHealthScore(h: {
  questions: number;
  users: number;
  payment: string;
  teamSetup: string;
}): { score: number; label: "Healthy" | "At Risk" | "Critical" } {
  let score = 0;
  score += Math.round(parseSetupPercent(h.teamSetup) * 30);

  const q = h.questions;
  if (q > 50000) score += 30;
  else if (q > 10000) score += 25;
  else if (q > 2000) score += 20;
  else if (q > 500) score += 15;
  else if (q > 100) score += 10;
  else if (q > 10) score += 5;

  if (h.payment.includes("Paid") && !h.payment.includes("Unpaid")) {
    score += 20;
  } else if (h.payment.includes("Trial") && !h.payment.includes("Expired")) {
    score += 10;
  }

  const u = h.users;
  if (u > 50) score += 20;
  else if (u > 20) score += 15;
  else if (u > 10) score += 10;
  else if (u > 3) score += 5;

  score = Math.min(100, Math.max(0, score));
  const label: "Healthy" | "At Risk" | "Critical" =
    score >= 70 ? "Healthy" : score >= 40 ? "At Risk" : "Critical";
  return { score, label };
}

function paymentString(l: LicenseHolderModel): string {
  if (((l as unknown) as { paid?: boolean }).paid === true) return "Paid";
  if (((l as unknown) as { paid?: boolean }).paid === false)
    return "Unpaid - Alert";
  return "Unknown";
}

// Convert raw API data into the UI's expected shape. Joins license
// holders with their usage rows (by licenseId) and aggregates per-email
// across multiple licenses an owner may have.
//
// Setup criteria match CustomerProfile.tsx exactly so the same account
// shows the same X/5 score in every place it appears (Customers list,
// Recent Signups table, Customer Engagement table, customer profile,
// health score input). Earlier versions used a placeholder that
// returned only 0/5 or 5/5 based on whether a teamId existed — that
// over-counted accounts which had created a team but had no players /
// plays / quizzes / lessons.
export function buildAdminHolders(
  licenses: LicenseHolderModel[],
  usage: LicenseUsageModel[]
): AdminLicenseHolder[] {
  const usageByLicense = new Map<string, LicenseUsageModel>();
  for (const u of usage) usageByLicense.set(u.licenseId, u);

  // Per-holder aggregates of the content counters needed for the 5
  // setup criteria. Tracked outside AdminLicenseHolder because we only
  // need them to compute setupCount; we don't surface them to the UI.
  interface SetupAccumulator {
    hasAnyTeam: boolean;
    totalPlays: number;
    totalCustomQuizzes: number;
    totalInstalls: number;
  }
  const setupByEmail = new Map<string, SetupAccumulator>();

  const byEmail = new Map<string, AdminLicenseHolder>();
  for (const l of licenses) {
    const key = (l.email || "").toLowerCase();
    if (!key) continue;
    const u = usageByLicense.get(l.id);
    const questions = Number(u?.numberOfQuestionsAnswered ?? 0);
    const coaches = Number(u?.numberOfCoaches ?? 0);
    const players = Number(u?.numberOfPlayers ?? 0);
    const plays = Number(u?.numberOfPlays ?? 0);
    const customQuizzes = Number(u?.numberOfCustomQuizzes ?? 0);
    const installs = Number(u?.numberOfInstalls ?? 0);
    const users = coaches + players;

    // Roll the content counters into the setup accumulator regardless
    // of whether this is the first or Nth license for the holder.
    const setupAcc = setupByEmail.get(key) ?? {
      hasAnyTeam: false,
      totalPlays: 0,
      totalCustomQuizzes: 0,
      totalInstalls: 0,
    };
    setupAcc.hasAnyTeam = setupAcc.hasAnyTeam || !!u?.teamId;
    setupAcc.totalPlays += plays;
    setupAcc.totalCustomQuizzes += customQuizzes;
    setupAcc.totalInstalls += installs;
    setupByEmail.set(key, setupAcc);

    const existing = byEmail.get(key);
    const payment = paymentString(l);

    const merged: AdminLicenseHolder = existing
      ? {
          ...existing,
          questions: existing.questions + questions,
          users: existing.users + users,
          totalCoaches: (existing.totalCoaches ?? 0) + coaches,
          totalPlayers: (existing.totalPlayers ?? 0) + players,
        }
      : {
          owner: `${l.firstName || ""} ${l.lastName || ""}`.trim() || l.email,
          email: l.email,
          tier: String(l.licenseTierId || ""),
          payment,
          hubspotPayment: ((l as unknown) as { hubspotPaymentStatus?: boolean })
            .hubspotPaymentStatus
            ? "Paid"
            : "Unpaid - Alert",
          tnRep: String(l.tnRepresentativeUserId || ""),
          dateCreated: String(
            ((l as unknown) as { created?: string }).created || ""
          ),
          users,
          questions,
          totalCoaches: coaches,
          totalPlayers: players,
          teamName: String(l.teamName || "-"),
          teamLevel: String(l.teamLevel || "-"),
          teamSetup: "0%", // populated below once all licenses are merged
          setupCount: 0,
          healthScore: 0,
          healthLabel: "Critical",
        };
    byEmail.set(key, merged);
  }

  // Now that all licenses for each holder have been merged, compute
  // the 5-criteria setup count. Matches CustomerProfile.tsx exactly.
  const result: AdminLicenseHolder[] = [];
  byEmail.forEach((h, key) => {
    const acc = setupByEmail.get(key);
    if (acc) {
      const totalPlayers = h.totalPlayers ?? 0;
      const setupCount =
        (acc.hasAnyTeam ? 1 : 0) +
        (totalPlayers >= 10 ? 1 : 0) +
        (acc.totalPlays >= 10 ? 1 : 0) +
        (acc.totalCustomQuizzes >= 10 ? 1 : 0) +
        (acc.totalInstalls >= 10 ? 1 : 0);
      h.setupCount = setupCount;
      h.teamSetup = `${setupCount * 20}%`;
    }
    const { score, label } = calculateHealthScore(h);
    h.healthScore = score;
    h.healthLabel = label;
    result.push(h);
  });
  return result;
}

export function buildPlatformMetrics(
  holders: AdminLicenseHolder[],
  fromApi: AnalyticsResponse["current"] | null
): AdminPlatformMetrics {
  const totalAccounts = fromApi?.totalAccounts ?? holders.length;
  const paidAccounts =
    fromApi?.paidAccounts ??
    holders.filter(
      (h) => h.payment.includes("Paid") && !h.payment.includes("Unpaid")
    ).length;
  const totalQuestions =
    fromApi?.totalQuestions ?? holders.reduce((sum, h) => sum + h.questions, 0);
  const totalUsers =
    fromApi?.totalUsers ?? holders.reduce((sum, h) => sum + h.users, 0);

  const healthyCount = holders.filter((h) => h.healthLabel === "Healthy")
    .length;
  const atRiskCount = holders.filter((h) => h.healthLabel === "At Risk").length;
  const criticalCount = holders.filter((h) => h.healthLabel === "Critical")
    .length;
  const avgHealthScore = holders.length
    ? Math.round(
        holders.reduce((sum, h) => sum + h.healthScore, 0) / holders.length
      )
    : 0;

  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const newThisMonth = holders.filter((h) => {
    const d = new Date(h.dateCreated);
    return !isNaN(d.getTime()) && d >= firstOfMonth;
  }).length;

  return {
    totalAccounts,
    paidAccounts,
    totalQuestions,
    totalUsers,
    avgHealthScore,
    healthyCount,
    atRiskCount,
    criticalCount,
    newThisMonth,
  };
}

export function buildHealthDistribution(
  holders: AdminLicenseHolder[]
): { range: string; count: number; category: string }[] {
  const buckets = [
    { range: "0-20", min: 0, max: 20, category: "critical" },
    { range: "21-40", min: 21, max: 40, category: "critical" },
    { range: "41-60", min: 41, max: 60, category: "atRisk" },
    { range: "61-80", min: 61, max: 80, category: "atRisk" },
    { range: "81-100", min: 81, max: 100, category: "healthy" },
  ];
  return buckets.map((b) => ({
    range: b.range,
    count: holders.filter(
      (h) => h.healthScore >= b.min && h.healthScore <= b.max
    ).length,
    category: b.category,
  }));
}

export function buildTierDistribution(
  holders: AdminLicenseHolder[]
): { tier: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const h of holders) {
    const key = friendlyTierName(h.tier) || "Unknown";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([tier, count]) => ({ tier, count }))
    .sort((a, b) => b.count - a.count);
}

export function buildTopAccounts(
  holders: AdminLicenseHolder[],
  limit: number
): AdminLicenseHolder[] {
  return holders
    .slice()
    .sort((a, b) => b.questions - a.questions)
    .slice(0, limit);
}

export function buildRecentSignups(
  holders: AdminLicenseHolder[],
  limit: number
): AdminLicenseHolder[] {
  return holders
    .slice()
    .sort((a, b) => {
      const ad = new Date(a.dateCreated).getTime() || 0;
      const bd = new Date(b.dateCreated).getTime() || 0;
      return bd - ad;
    })
    .slice(0, limit);
}

export interface AdminAnalyticsBundle {
  metrics: AdminPlatformMetrics;
  holders: AdminLicenseHolder[];
  snapshots: AdminSnapshotSummary[];
  healthDist: ReturnType<typeof buildHealthDistribution>;
  tierDist: ReturnType<typeof buildTierDistribution>;
  topAccounts: AdminLicenseHolder[];
  recentSignups: AdminLicenseHolder[];
}

export async function loadAdminAnalytics(): Promise<AdminAnalyticsBundle> {
  // Load core data + engagement in parallel. Engagement is best-effort —
  // if the endpoint fails (e.g., during a partial deploy), the page still
  // renders with `—` for the new columns rather than blocking.
  const [analytics, licenses, usage, engagement] = await Promise.all([
    APIService.ADMIN_ANALYTICS.GET_ANALYTICS(),
    APIService.LICENSE.LIST() as Promise<LicenseHolderModel[]>,
    APIService.LICENSE_QUOTA.LIST() as Promise<LicenseUsageModel[]>,
    APIService.ADMIN_ANALYTICS.GET_ENGAGEMENT().catch(() => ({ rows: [] })),
  ]);
  const holders = buildAdminHolders(licenses, usage);
  mergeEngagement(holders, engagement.rows);
  const metrics = buildPlatformMetrics(holders, analytics.current);
  // Top 10 sorts by 7-day questions when engagement data is available — the
  // user wants "who's using it RIGHT NOW", not all-time leaders. Falls back
  // to all-time questions when 7d data isn't loaded yet.
  return {
    metrics,
    holders,
    snapshots: analytics.snapshots,
    healthDist: buildHealthDistribution(holders),
    tierDist: buildTierDistribution(holders),
    topAccounts: buildTopAccountsByRecentEngagement(holders, 10),
    recentSignups: buildRecentSignups(holders, 10),
  };
}

export async function loadCustomerDetail(
  email: string
): Promise<CustomerDetailResponse> {
  return APIService.ADMIN_ANALYTICS.GET_CUSTOMER(email);
}

export async function loadCustomerEngagement(
  email: string
): Promise<EngagementRow | null> {
  try {
    const all = await APIService.ADMIN_ANALYTICS.GET_ENGAGEMENT();
    const lower = email.toLowerCase();
    return all.rows.find((r) => r.email === lower) ?? null;
  } catch {
    return null;
  }
}

function mergeEngagement(
  holders: AdminLicenseHolder[],
  rows: EngagementRow[]
): void {
  const byEmail = new Map<string, EngagementRow>();
  for (const r of rows) byEmail.set(r.email.toLowerCase(), r);
  for (const h of holders) {
    const r = byEmail.get(h.email.toLowerCase());
    if (!r) continue;
    h.questions7d = r.questions7d;
    h.questions60d = r.questions60d;
    h.questions90d = r.questions90d;
    h.questionsPrev90d = r.questionsPrev90d;
    h.questions1y = r.questions1y;
    h.shopifyPaidTotal = r.shopifyPaidTotal;
    h.shopifyLink = r.shopifyLink;
    h.hubspotLink = r.hubspotLink;
    h.teamCity = r.teamCity;
    h.teamState = r.teamState;
    h.lastCoachActivityAt = r.lastCoachActivityAt;
    h.lastPlayerActivityAt = r.lastPlayerActivityAt;
    // Engagement endpoint's coach/player split is authoritative — it
    // queries license_usage directly with no per-license dedupe issues.
    h.totalCoaches = r.totalCoaches;
    h.totalPlayers = r.totalPlayers;
    h.commsOutbound = r.commsOutbound;
    h.commsInbound = r.commsInbound;
    h.commsLogged = r.commsLogged;
    h.commsTotal = r.commsTotal;
  }
}

// Top 10 ordered by recent engagement. Prefer 7-day questions if any
// holder has them; otherwise fall back to all-time. This matches "who's
// using it RIGHT NOW" while still working before the engagement endpoint
// is deployed.
export function buildTopAccountsByRecentEngagement(
  holders: AdminLicenseHolder[],
  limit: number
): AdminLicenseHolder[] {
  const hasRecent = holders.some(
    (h) => typeof h.questions7d === "number" && h.questions7d > 0
  );
  return holders
    .slice()
    .sort((a, b) => {
      if (hasRecent) {
        return (b.questions7d ?? 0) - (a.questions7d ?? 0);
      }
      return b.questions - a.questions;
    })
    .slice(0, limit);
}
